//? React
import { useState } from "react";

//? External
import { Offcanvas, Button, Spinner } from "react-bootstrap";
import { FaTrashAlt, FaListUl } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

//* Data hook
import { useQuestionCart } from "../../hooks/questionCart/useQuestionCart";

interface QuestionCartDrawerProps {
  show: boolean;
  onHide: () => void;
}

/**
 * Lateral drawer listing the items currently in the question cart.
 */
export default function QuestionCartDrawer({ show, onHide }: QuestionCartDrawerProps) {
  const token = useSelector((state: any) => state.auth.token);
  const navigate = useNavigate();

  const { items, loading, isError, removeFromCart, clearCart, isClearing } = useQuestionCart(token);

  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = async (itemId: string) => {
    setRemovingId(itemId);
    try {
      await removeFromCart(itemId);
    } catch (error) {
      console.error("Erro ao remover item da lista:", error);
    } finally {
      setRemovingId(null);
    }
  };

  const handleClear = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error("Erro ao limpar a lista:", error);
    }
  };

  const handleAddToTest = () => {
    onHide();
    navigate("/exams");
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <FaListUl className="me-2" />
          Lista de Questões
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="d-flex flex-column gap-3">
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : isError ? (
          <p className="text-danger">Erro ao carregar a lista.</p>
        ) : items.length === 0 ? (
          <div className="text-center text-muted py-4">
            <FaListUl size={32} className="mb-2" />
            <p>Sua lista está vazia.</p>
          </div>
        ) : (
          <>
            <ul className="list-unstyled d-flex flex-column gap-2">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="d-flex justify-content-between align-items-center border rounded p-2"
                >
                  <div className="d-flex flex-column">
                    <strong>{item.question.displayLabel ?? "Questão"}</strong>
                    <small className="text-muted">
                      {item.question.discipline?.name ?? "-"}
                      {item.question.source ? ` · ${item.question.source}` : ""}
                    </small>
                  </div>
                  <Button
                    id={`btn-remove-cart-${item.id}`}
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    aria-label="Remover questão da lista"
                    disabled={removingId === item.id}
                    onClick={() => handleRemove(item.id)}
                  >
                    <FaTrashAlt />
                  </Button>
                </li>
              ))}
            </ul>

            <div className="mt-auto d-flex flex-column gap-2">
              <Button
                id="btn-add-cart-to-test"
                type="button"
                variant="primary"
                onClick={handleAddToTest}
              >
                Adicionar à prova
              </Button>
              <Button
                id="btn-clear-cart"
                type="button"
                variant="outline-secondary"
                disabled={isClearing}
                onClick={handleClear}
              >
                {isClearing ? "Limpando..." : "Limpar lista"}
              </Button>
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
